import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { pick } from 'ramda';
import { getCleanUrl } from '@theme/js/utils/url.util';
import { componentService } from '@core/js/services/component.service';
import { useAppSelector } from '@core/js/store/hooks';
import { useNavigate } from '@theme/js/hooks/navigate';
import { findPathByCleanUrl } from './utils/findPathByCleanUrl';

import * as Types from './types';
import style from './BreadcrumbList.scss';

export const BreadcrumbList: React.FC<Types.IBreadcrumbListProps> = ({ color = 'white' }) => {
    const navigate = useNavigate();
    const { rootPage, menuItems } = useAppSelector(pick(['rootPage', 'menuItems']));
    const { pathname } = useLocation();
    const cleanUrl = useMemo(
        () => getCleanUrl(pathname) === '/' ? rootPage : getCleanUrl(pathname),
        [pathname, rootPage]
    );
    const currentMenuItems = useMemo(
        () => findPathByCleanUrl(cleanUrl, menuItems as Types.IMenuItem[]),
        [cleanUrl, menuItems]
    );

    const breadcrumbList = useMemo(() => {
        if (!currentMenuItems) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: currentMenuItems.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item?.name,
                ...index !== currentMenuItems.length - 1 && {
                    item: `${window?.location?.origin}${item?.friendlyURL}`
                }
            }))
        };
    }, [currentMenuItems]);

    useEffect(() => {
        if (!breadcrumbList) {
            return;
        }

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(breadcrumbList);

        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, [breadcrumbList]);

    const handleLinkNavigate = useCallback((event: React.MouseEvent<HTMLAnchorElement>, url?: string) => {
        event.preventDefault();
        navigate(url || '/');
    }, [navigate]);

    if (!currentMenuItems) {
        return null;
    }

    return (
        <ul className={`${style.breadcrumb} ${style[color]}`}>
        {currentMenuItems.map((item, index) => (
            <li className={style.breadcrumbItem} key={item.friendlyURL || index}>
                {index < currentMenuItems?.length - 1 ? (
                    <>
                        <a
                            className={style.breadcrumbLink}
                            href={item?.friendlyURL}
                            onClick={(event) => handleLinkNavigate(event, item?.friendlyURL)}
                        >
                            {item?.name}
                        </a>
                        <span className={`${style.breadcrumbIcon} list-item_link-icon icon-ico-s4w-arrow-right`} />
                    </>
                ) : (
                    <span className={style.current}>{item?.name}</span>
                )}
            </li>
        ))}
    </ul>
    );
};

componentService.registerWebContentComponents({ BreadcrumbList });
